import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Switch, Tooltip, IconButton, Collapse, Paper, TextField } from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Navbar from "../components/Navbar";
import { FetchCoreData } from "../controllers/apirequest";
import { marked } from "marked";
import DOMPurify from "dompurify";

interface Order {
  id: number;
  title: string;
  summary: string;
  description: string;
  isActive: boolean;
  releasedAt: string;
  updatedAt: string;
}

// Render an order's description the same way the PRP-Core preview shows it:
// every line break is kept, and numbered lines stay as typed ("4." stays 4.)
// instead of being turned into lists that restart at 1.
const renderOrderDescription = (text: string): string => {
  const source = (text || "No description available")
    .replace(/\r\n?/g, "\n")
    .replace(/^(\s*(?:[-*+]\s+)?\d+)([.)])(?=\s)/gm, "$1\\$2");
  const html = marked.parse(source, { async: false, gfm: true, breaks: true }) as string;
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "h1", "h2", "h3", "b", "i", "em", "strong", "a", "p", "ul", "ol", "li", "code", "pre", "blockquote", "br"
    ],
    ALLOWED_ATTR: ["href", "title", "start"]
  });
};

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        await FetchCoreData(
          () => {},
          (orders: any[]) => {
            if (!orders || !Array.isArray(orders) || orders.length === 0) {
              setOrders([]);
              return;
            }
            setOrders(orders);
          }
        );
      } catch (error) {
        console.error("Failed to fetch orders", error);
      }
    };

    fetchOrders();
  }, []);

  const handleExpand = (orderId: number) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const handleInactiveSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowInactive(event.target.checked);
  };

  // Search filter
  const filteredOrders = (showInactive ? orders : orders.filter((order) => order.isActive))
    .filter((order) => {
      if (!search.trim()) return true;
      const searchLower = search.toLowerCase();
      return (
        order.title.toLowerCase().includes(searchLower) ||
        order.summary.toLowerCase().includes(searchLower) ||
        order.description.toLowerCase().includes(searchLower) ||
        new Date(order.releasedAt).toLocaleString().toLowerCase().includes(searchLower) ||
        new Date(order.updatedAt).toLocaleString().toLowerCase().includes(searchLower)
      );
    });

  // Sort by releasedAt descending (memoized)
  const sortedOrders = useMemo(() => {
    return [...filteredOrders].sort((a, b) => new Date(b.releasedAt).getTime() - new Date(a.releasedAt).getTime());
  }, [filteredOrders]);

  // Find most recent released and updated active orders
  const activeSortedOrders = sortedOrders.filter(order => order.isActive);
  const mostRecentActiveOrderFromSorted = activeSortedOrders.length > 0
    ? activeSortedOrders[0]
    : undefined;
  const mostRecentlyUpdatedOrder = activeSortedOrders.length > 1
    ? activeSortedOrders
        .filter(order => order.id !== mostRecentActiveOrderFromSorted?.id)
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0]
    : undefined;

  // Organize orders: most recent released, then most recent updated, then the rest
  let orderedRows: Order[] = [];
  if (mostRecentActiveOrderFromSorted) {
    orderedRows.push(mostRecentActiveOrderFromSorted);
    if (mostRecentlyUpdatedOrder) {
      orderedRows.push(mostRecentlyUpdatedOrder);
    }
    const rest = sortedOrders.filter(
      (order) => order.id !== mostRecentActiveOrderFromSorted.id && order.id !== mostRecentlyUpdatedOrder?.id
    );
    orderedRows = [...orderedRows, ...rest];
  } else {
    orderedRows = sortedOrders;
  }

  return (
    <Box sx={{ px: 5, py: 3 }}>
      <Navbar />
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
        <Paper elevation={0} sx={{ width: "100%", maxWidth: 1200, px: { xs: 2.5, md: 4 }, py: 3, mb: 1, mt: 4, borderRadius: 3, backgroundColor: "var(--surface)", backgroundImage: "none", border: "1px solid var(--border)" }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", gap: 2, width: "100%" }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#fff" }}>
                DOJ Orders
              </Typography>
              <Typography sx={{ color: "var(--muted)", fontSize: "0.9rem", mt: 0.5 }}>
                Standing orders from the Supreme Court of San Andreas. Click an order to read it in full.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <TextField
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search orders..."
                size="small"
                sx={{ width: 240, backgroundColor: "var(--backgroundcolor1)", borderRadius: 2, input: { color: "#fff" } }}
                InputProps={{ style: { color: "#fff" } }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch
                  checked={showInactive}
                  onChange={handleInactiveSwitch}
                  color="info"
                  inputProps={{ 'aria-label': 'Show inactive orders' }}
                />
                <Typography sx={{ color: "var(--muted)", fontSize: "0.9rem", whiteSpace: "nowrap" }}>
                  {showInactive ? "Show Inactive" : "Hide Inactive"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
      <Paper elevation={0} sx={{ maxWidth: "1200px", margin: "0 auto", overflowX: "auto", borderRadius: 3, backgroundColor: "var(--surface)", backgroundImage: "none", border: "1px solid var(--border)" }}>
        <Table sx={{
          "& .MuiTableCell-root": { borderBottom: "1px solid var(--border)" },
          "& .MuiTableHead-root .MuiTableCell-root": { color: "var(--muted)", fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", backgroundColor: "var(--surface-raised)" },
        }}>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Released At</TableCell>
              <TableCell>Updated At</TableCell>
              <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Status
                <Tooltip
                  title={
                    <Box>
                      <Typography sx={{ fontSize: '0.85rem', color: '#fff', display: 'flex', alignItems: 'center', mb: 0.3, lineHeight: 1 }}>
                        <span style={{ color: '#4fc3f7', fontWeight: 700, fontSize: '0.85rem' }}>Blue edge</span>
                        <span style={{ margin: '0 4px', fontSize: '0.85rem' }}>=</span>
                        <span style={{ fontSize: '0.85rem' }}>Most recently released order</span>
                      </Typography>
                      <Typography sx={{ fontSize: '0.85rem', color: '#fff', display: 'flex', alignItems: 'center', lineHeight: 1 }}>
                        <span style={{ color: '#ff9800', fontWeight: 700, fontSize: '0.85rem' }}>Orange edge</span>
                        <span style={{ margin: '0 4px', fontSize: '0.85rem' }}>=</span>
                        <span style={{ fontSize: '0.85rem' }}>Most recently updated order (excluding most recent released)</span>
                      </Typography>
                    </Box>
                  }
                >
                  <IconButton sx={{ color: "#4fc3f7", p: 0, ml: 1, alignSelf: 'center', height: 24 }} size="small">
                    <InfoOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderedRows.map((order: Order) => (
              <React.Fragment key={order.id}>
                <TableRow
                  sx={{
                    boxShadow: order.id === mostRecentActiveOrderFromSorted?.id
                      ? "inset 4px 0 0 #4fc3f7"
                      : order.id === mostRecentlyUpdatedOrder?.id
                        ? "inset 4px 0 0 #ff9800"
                        : undefined,
                    backgroundColor: expandedOrderId === order.id ? "var(--surface-raised)" : "inherit",
                    cursor: "pointer",
                    transition: "background-color .15s",
                    "&:hover": { backgroundColor: "var(--surface-raised)" },
                    "& > td:first-of-type": { fontWeight: 600, color: "#fff" },
                    "& > td": { color: "#c9d3df" },
                  }}
                  onClick={() => handleExpand(order.id)}
                >
                  <TableCell>{order.title}</TableCell>
                  <TableCell>{order.summary || "No summary available"}</TableCell>
                  <TableCell>{
                    order.releasedAt && !isNaN(Date.parse(order.releasedAt))
                      ? new Date(order.releasedAt).toLocaleString()
                      : "—"
                  }</TableCell>
                  <TableCell>{
                    order.updatedAt && !isNaN(Date.parse(order.updatedAt)) && order.releasedAt && !isNaN(Date.parse(order.releasedAt)) &&
                    new Date(order.updatedAt).getTime() !== new Date(order.releasedAt).getTime()
                      ? new Date(order.updatedAt).toLocaleString()
                      : "Never updated"
                  }</TableCell>
                  <TableCell>
                    <Box component="span" sx={{
                      display: "inline-block", px: 1.25, py: 0.25, borderRadius: 999, fontSize: "0.75rem", fontWeight: 600,
                      color: order.isActive ? "#4ade80" : "#f87171",
                      backgroundColor: order.isActive ? "rgba(74, 222, 128, .12)" : "rgba(248, 113, 113, .12)",
                      border: `1px solid ${order.isActive ? "rgba(74, 222, 128, .35)" : "rgba(248, 113, 113, .35)"}`,
                    }}>
                      {order.isActive ? "Active" : "Inactive"}
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={5} sx={{ p: 0, ...(expandedOrderId === order.id ? {} : { borderBottom: "none !important" }) }}>
                    <Collapse in={expandedOrderId === order.id} timeout="auto" unmountOnExit>
                      <Box sx={{ px: { xs: 2.5, md: 5 }, py: 3, backgroundColor: "var(--backgroundcolor1)", borderTop: "1px solid var(--border)", color: "#dbe3ec", lineHeight: 1.7, "& > div": { maxWidth: 900 } }}>
                        <Typography
                          component="div"
                          sx={{
                            "& h1": { fontSize: "2rem", fontWeight: 700, m: "16px 0 8px" },
                            "& h2": { fontSize: "1.5rem", fontWeight: 700, m: "14px 0 6px" },
                            "& h3": { fontSize: "1.25rem", fontWeight: 700, m: "12px 0 6px" },
                            "& p": { m: "0 0 12px" },
                          }}
                          dangerouslySetInnerHTML={{
                            __html: renderOrderDescription(order.description),
                          }}
                        />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default Orders;
